import { useEffect, useState, useCallback, useRef } from "react";
import { Loader2, Plus, Download, Upload, KeyRound, ShieldAlert, Clock, Mail, RotateCcw, Printer, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PageHeader, FilterSelect } from "@/components/common";
import { api, errMsg, download } from "@/lib/api";
import { fmtDateTime, ROLE_LABEL } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const ROLE_OPTS = Object.entries(ROLE_LABEL).map(([value, label]) => ({ value, label }));

export function MyAccount() {
  const { user } = useAuth();
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (f.new_password !== f.confirm) return toast.error("New passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      toast.success("Password changed"); setF({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-xl border p-5 max-w-lg space-y-4">
      <div><div className="font-semibold">{user.name}</div><div className="text-sm text-slate-500">{user.email} · {ROLE_LABEL[user.role]}</div></div>
      {[["current_password", "Current password"], ["new_password", "New password (min 8 characters)"], ["confirm", "Confirm new password"]].map(([k, l]) => (
        <div key={k}><Label>{l}</Label><Input type="password" className="mt-1.5" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`account-${k}`} /></div>
      ))}
      <Button className="bg-blue-800 hover:bg-blue-900" onClick={save} disabled={busy || !f.current_password || !f.new_password} data-testid="account-save-btn"><KeyRound className="h-4 w-4" /> Change password</Button>
    </div>
  );
}

function UserDialog({ open, onOpenChange, user, onSaved }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setF(user ? { name: user.name, role: user.role, active: user.active, password: "" } : { email: "", name: "", role: "payroll", password: "" });
  }, [user, open]);
  const save = async () => {
    setBusy(true);
    try {
      if (user) await api.put(`/users/${user.id}`, { ...f, password: f.password || null });
      else await api.post("/users", f);
      toast.success(user ? "User updated" : "User created"); onSaved(); onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="user-dialog">
        <DialogHeader><DialogTitle>{user ? `Edit ${user.email}` : "Add user"}</DialogTitle><DialogDescription>Users sign in with email and password. Only administrators can manage users.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          {!user && <div><Label>Email</Label><Input className="mt-1.5" type="email" value={f.email || ""} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-form-email" /></div>}
          <div><Label>Name</Label><Input className="mt-1.5" value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-form-name" /></div>
          <div><Label>Role</Label><div className="mt-1.5"><FilterSelect value={f.role} onChange={(v) => setF({ ...f, role: v || "readonly" })} options={ROLE_OPTS} placeholder="Select role" testid="user-form-role" className="w-full" /></div></div>
          <div><Label>{user ? "Reset password (leave blank to keep)" : "Password (min 8 characters)"}</Label><Input className="mt-1.5" type="password" value={f.password || ""} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="user-form-password" /></div>
          {user && <label className="flex items-center gap-2 text-sm"><Switch checked={!!f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="user-form-active" /> Account active</label>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-blue-800 hover:bg-blue-900" disabled={busy} onClick={save} data-testid="user-form-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmailDialog({ user, onClose, onSaved }) {
  const [email, setEmail] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setEmail(""); setConfirm(false); }, [user]);
  const clean = email.trim().toLowerCase();
  const invalid = clean && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(clean);
  const same = user && clean === user.email;
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/users/${user.id}/email`, { email: clean });
      toast.success(`Email changed to ${clean}. ${user.name} must now sign in with the new email.`);
      onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); setConfirm(false); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="email-dialog">
        <DialogHeader><DialogTitle>Change login email</DialogTitle><DialogDescription>{user?.name} · {ROLE_LABEL[user?.role]}. The account, role and all records stay the same. Only the sign-in email changes.</DialogDescription></DialogHeader>
        {!confirm ? (
          <div className="space-y-3">
            <div><Label>Current email</Label><Input className="mt-1.5 bg-slate-50" value={user?.email || ""} readOnly data-testid="email-current" /></div>
            <div>
              <Label>New email</Label>
              <Input className={`mt-1.5 ${invalid || same ? "border-red-500" : ""}`} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" data-testid="email-new-input" />
              {(invalid || same) && <div className="text-xs text-red-700 mt-1" data-testid="email-new-error">{same ? "This is already the current email" : "Please enter a valid email address"}</div>}
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 space-y-1" data-testid="email-confirm-box">
            <div>Change the sign-in email of <b>{user?.name}</b>?</div>
            <div className="money">{user?.email} → <b>{clean}</b></div>
            <div className="text-xs">The old email will stop working for login immediately.</div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => (confirm ? setConfirm(false) : onClose())} data-testid="email-cancel-btn">{confirm ? "Back" : "Cancel"}</Button>
          {!confirm
            ? <Button className="bg-blue-800 hover:bg-blue-900" disabled={!clean || invalid || same} onClick={() => setConfirm(true)} data-testid="email-continue-btn">Continue</Button>
            : <Button className="!bg-amber-600 hover:!bg-amber-700 text-white" disabled={busy} onClick={save} data-testid="email-confirm-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Confirm change</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UsersTab() {
  const [rows, setRows] = useState([]);
  const [dlg, setDlg] = useState({ open: false, user: null });
  const [emailUser, setEmailUser] = useState(null);
  const load = useCallback(() => { api.get("/users").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-3">
      <Button className="bg-blue-800 hover:bg-blue-900" onClick={() => setDlg({ open: true, user: null })} data-testid="add-user-btn"><Plus className="h-4 w-4" /> Add user</Button>
      <div className="bg-white rounded-xl border overflow-x-auto" data-testid="users-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500"><tr><th className="px-4 py-2 text-left">Name</th><th className="px-4 py-2 text-left">Email</th><th className="px-4 py-2 text-left">Role</th><th className="px-4 py-2 text-left">Status</th><th className="px-4 py-2" /></tr></thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} className="border-t">
                <td className="px-4 py-2.5 font-medium">{u.name}</td><td className="px-4 py-2.5">{u.email}</td><td className="px-4 py-2.5">{ROLE_LABEL[u.role]}</td>
                <td className="px-4 py-2.5"><span className={`text-xs rounded-full px-2 py-0.5 ${u.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{u.active ? "Active" : "Disabled"}</span></td>
                <td className="px-4 py-2.5 text-right whitespace-nowrap"><Button variant="ghost" size="sm" onClick={() => setEmailUser(u)} data-testid={`edit-email-${u.email}`}><Mail className="h-3.5 w-3.5" /> Edit Email</Button><Button variant="ghost" size="sm" onClick={() => setDlg({ open: true, user: u })} data-testid={`edit-user-${u.email}`}>Edit</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <UserDialog open={dlg.open} user={dlg.user} onOpenChange={(o) => setDlg((d) => ({ ...d, open: o }))} onSaved={load} />
      <EmailDialog user={emailUser} onClose={() => setEmailUser(null)} onSaved={load} />
    </div>
  );
}

function AuditTab() {
  const [action, setAction] = useState("");
  const [q, setQ] = useState("");
  const [skip, setSkip] = useState(0);
  const [d, setD] = useState({ total: 0, items: [], actions: [] });
  useEffect(() => {
    const t = setTimeout(() => api.get("/audit", { params: { action, q, skip, limit: 50 } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))), 200);
    return () => clearTimeout(t);
  }, [action, q, skip]);
  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row gap-2">
        <Input placeholder="Search details or user" value={q} onChange={(e) => { setQ(e.target.value); setSkip(0); }} className="sm:w-72 bg-white" data-testid="audit-search" />
        <FilterSelect value={action} onChange={(v) => { setAction(v); setSkip(0); }} options={d.actions.map((a) => ({ value: a, label: a.replace(/_/g, " ") }))} placeholder="All actions" testid="audit-action-filter" className="sm:w-56" />
      </div>
      <div className="bg-white rounded-xl border overflow-x-auto" data-testid="audit-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500"><tr><th className="px-4 py-2 text-left">When</th><th className="px-4 py-2 text-left">User</th><th className="px-4 py-2 text-left">Action</th><th className="px-4 py-2 text-left">Details</th></tr></thead>
          <tbody>
            {d.items.length === 0 && <tr><td colSpan={4} className="text-center text-slate-500 py-8">No audit entries.</td></tr>}
            {d.items.map((a) => (
              <tr key={a.id} className="border-t align-top">
                <td className="px-4 py-2 whitespace-nowrap text-xs">{fmtDateTime(a.at)}</td><td className="px-4 py-2 text-xs">{a.user_email}</td>
                <td className="px-4 py-2"><span className="text-xs rounded bg-slate-100 px-1.5 py-0.5 whitespace-nowrap">{a.action.replace(/_/g, " ")}</span></td><td className="px-4 py-2 text-xs text-slate-700">{a.details}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between text-sm text-slate-500">
        <span>{d.total} entries</span>
        <div className="flex gap-2"><Button variant="outline" size="sm" disabled={skip === 0} onClick={() => setSkip(skip - 50)}>Previous</Button><Button variant="outline" size="sm" disabled={skip + 50 >= d.total} onClick={() => setSkip(skip + 50)}>Next</Button></div>
      </div>
    </div>
  );
}

function PrintingTab() {
  return (
    <div className="bg-white rounded-xl border p-5 max-w-3xl" data-testid="qz-printing-card">
      <div className="font-semibold flex items-center gap-2"><Printer className="h-4 w-4" /> Direct slip printing (QZ Tray)</div>
      <p className="text-sm text-slate-500 mt-1 mb-4">The app signs every print request with BSHPL's own certificate. Trust it once on each outlet computer and slips will print straight to the 80mm printer with no "Allow" pop-up.</p>
      <Button className="bg-blue-800 hover:bg-blue-900" onClick={() => download("/qz/override.crt").catch(() => toast.error("Download failed"))} data-testid="qz-cert-download-btn"><Download className="h-4 w-4" /> Download certificate (override.crt)</Button>
      <ol className="list-decimal pl-5 mt-4 space-y-1.5 text-sm text-slate-700" data-testid="qz-install-steps">
        <li>Install QZ Tray on the outlet computer (qz.io/download) and set the 80mm printer as the default printer.</li>
        <li>Copy the downloaded <b>override.crt</b> into the QZ Tray folder — Windows: <code>C:\Program Files\QZ Tray\</code>, Mac: <code>/Applications/QZ Tray.app/Contents/Resources/</code>, Linux: <code>/opt/qz-tray/</code>. Keep the file name exactly <b>override.crt</b>.</li>
        <li>Quit QZ Tray from the system tray and open it again.</li>
        <li>Print one slip from Add Advance — it goes straight to the printer. If a prompt still appears once, tick “Remember this decision” and click Allow.</li>
      </ol>
      <p className="text-xs text-slate-500 mt-4">Computers without QZ Tray keep using the normal print window. Download the same file for every outlet; it stays valid for 30 years.</p>
    </div>
  );
}

function BackupTab() {
  const ref = useRef();
  const [file, setFile] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const restore = async () => {
    setBusy(true);
    const fd = new FormData(); fd.append("file", file); fd.append("confirm", confirm);
    try {
      const { data } = await api.post("/backup/restore", fd);
      toast.success(`Restored ${data.employees} employees and ${data.transactions} transactions`); setFile(null); setConfirm("");
      window.dispatchEvent(new Event("backups:changed"));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-4 [&>*]:min-w-0">
      <div className="bg-white rounded-xl border p-5">
        <div className="font-semibold">Download full backup</div>
        <p className="text-sm text-slate-500 mt-1 mb-4">All employees, transactions, payrolls, deduction uploads, users and the audit trail in one file. Keep it somewhere safe.</p>
        <Button className="bg-blue-800 hover:bg-blue-900" onClick={() => download("/backup").catch(() => toast.error("Backup failed"))} data-testid="backup-download-btn"><Download className="h-4 w-4" /> Download backup</Button>
      </div>
      <div className="bg-white rounded-xl border border-red-200 p-5">
        <div className="font-semibold flex items-center gap-2 text-red-800"><ShieldAlert className="h-4 w-4" /> Restore from backup</div>
        <p className="text-sm text-slate-500 mt-1 mb-4">Replaces ALL current employees, transactions, payrolls, deduction uploads and audit logs with the backup file. Users missing from the app are added back with their passwords; existing users are kept as they are. A safety backup of the current data is saved automatically first.</p>
        <input ref={ref} type="file" accept=".json" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="restore-file-input" />
        <Button variant="outline" onClick={() => ref.current?.click()} data-testid="restore-choose-btn"><Upload className="h-4 w-4" /> {file ? file.name : "Choose backup file"}</Button>
        {file && (
          <div className="mt-3 space-y-2">
            <Label>Type RESTORE to confirm</Label>
            <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="restore-confirm-input" />
            <Button className="bg-red-700 hover:bg-red-800" disabled={busy || confirm !== "RESTORE"} onClick={restore} data-testid="restore-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Restore now</Button>
          </div>
        )}
      </div>
      <EmailSettings />
      <StoredBackups />
      <RestoreCheck />
    </div>
  );
}

function EmailSettings() {
  const [cfg, setCfg] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/settings/backup-email").then((r) => { setCfg(r.data); setText(r.data.recipients.join(", ")); }).catch((e) => toast.error(errMsg(e)));
  }, []);
  const save = async (enabled) => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/backup-email", { enabled, recipients: text.split(/[,;\s]+/).filter(Boolean) });
      setCfg(data); setText(data.recipients.join(", ")); toast.success("Backup email settings saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!cfg) return null;
  return (
    <div className="md:col-span-2 bg-white rounded-xl border p-5" data-testid="backup-email-settings">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div>
          <div className="font-semibold flex items-center gap-2"><Mail className="h-4 w-4 text-blue-800" /> Email a copy every night</div>
          <p className="text-sm text-slate-500 mt-1">Each nightly backup email includes a secure "Download backup file" button that works for 7 days and only for Administrators. Every backup is a full snapshot (all data from the start) and is also kept in secure cloud storage, separate from the website database. User accounts are included with their encrypted passwords.</p>
        </div>
        <label className="flex items-center gap-2 text-sm whitespace-nowrap">
          <Switch checked={cfg.enabled} disabled={busy} onCheckedChange={(v) => save(v)} data-testid="backup-email-toggle" />
          <span className={cfg.enabled ? "text-emerald-700 font-medium" : "text-slate-500"} data-testid="backup-email-status">{cfg.enabled ? "On" : "Off"}</span>
        </label>
      </div>
      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        <Input placeholder="accounts@company.com, owner@company.com" value={text} onChange={(e) => setText(e.target.value)} className="flex-1" data-testid="backup-email-recipients" />
        <Button className="bg-blue-800 hover:bg-blue-900" disabled={busy} onClick={() => save(cfg.enabled)} data-testid="backup-email-save">Save recipients</Button>
      </div>
      <div className="text-xs text-slate-500 mt-2">Up to 5 addresses, separated by commas.{cfg.updated_by && ` Last changed by ${cfg.updated_by} on ${fmtDateTime(cfg.updated_at)}.`}</div>
    </div>
  );
}

function EmailStatus({ b }) {
  if (!b.email_status) return <span className="text-xs text-slate-400">—</span>;
  const ok = b.email_status === "sent";
  return <span title={b.email_status} className={`text-xs rounded-full px-2 py-0.5 ${ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>{ok ? "Emailed" : "Email failed"}</span>;
}

const KIND = { auto: ["Nightly", "bg-blue-50 text-blue-800"], manual: ["Manual", "bg-slate-100 text-slate-600"], "pre-restore": ["Before restore", "bg-amber-50 text-amber-800"] };

function RestoreStoredDialog({ backup, onOpenChange, onDone }) {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setConfirm(""); }, [backup]);
  const run = async () => {
    setBusy(true);
    const fd = new FormData(); fd.append("confirm", confirm);
    try {
      const { data } = await api.post(`/backups/${backup.id}/restore`, fd);
      toast.success(`Restored ${data.employees} employees and ${data.transactions} transactions. Previous data saved as a 'Before restore' backup.`);
      onOpenChange(false); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!backup} onOpenChange={onOpenChange}>
      <DialogContent data-testid="restore-stored-dialog">
        <DialogHeader>
          <DialogTitle className="text-red-800">Restore this backup?</DialogTitle>
          <DialogDescription>All current employees, transactions and audit logs will be replaced. The current data is saved first as a "Before restore" backup, so you can undo this.</DialogDescription>
        </DialogHeader>
        {backup && (
          <div className="rounded-lg bg-slate-50 p-3 text-sm grid grid-cols-2 sm:grid-cols-[1.6fr_1fr_1fr] gap-2">
            <div className="col-span-2 sm:col-span-1"><div className="text-xs text-slate-500">Created</div><div className="whitespace-nowrap">{fmtDateTime(backup.created_at)}</div></div>
            <div><div className="text-xs text-slate-500">Employees</div><div className="money">{backup.employees}</div></div>
            <div><div className="text-xs text-slate-500">Transactions</div><div className="money">{backup.transactions}</div></div>
          </div>
        )}
        <div><Label>Type RESTORE to confirm</Label><Input className="mt-1.5" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="restore-stored-confirm-input" /></div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-red-700 hover:bg-red-800" disabled={busy || confirm !== "RESTORE"} onClick={run} data-testid="restore-stored-confirm-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Restore now</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RestoreCheck() {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/backups/restore-check").then((r) => setC(r.data)).catch(() => setC({})); }, []);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/backups/restore-check"); setC(data); data.status === "passed" ? toast.success("Restore check passed") : toast.error(data.details); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const ok = c?.status === "passed";
  return (
    <div className="md:col-span-2 bg-white rounded-xl border p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="restore-check-card">
      <div className="flex-1">
        <div className="font-semibold flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-blue-800" /> Monthly practice restore check</div>
        <p className="text-sm text-slate-500 mt-1">On the 2nd of every month at 3:00 AM IST the app opens the latest cloud backup and confirms it would restore correctly, without changing any data.</p>
        <p className="text-sm mt-2" data-testid="restore-check-result">
          {!c?.status ? <span className="text-slate-500">No check has run yet.</span> : (
            <><span className={`text-xs rounded-full px-2 py-0.5 mr-2 ${ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>{ok ? "Passed" : "Failed"}</span>
              {new Date(c.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "medium" })}{c.backup_created_at && ` · backup of ${fmtDateTime(c.backup_created_at)}`} · {c.details}</>
          )}
        </p>
      </div>
      <Button variant="outline" onClick={run} disabled={busy} data-testid="restore-check-run-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Run check now</Button>
    </div>
  );
}

function StoredBackups() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState("");
  const [restoring, setRestoring] = useState(null);
  const load = useCallback(() => { api.get("/backups").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => {
    load();
    window.addEventListener("backups:changed", load);
    return () => window.removeEventListener("backups:changed", load);
  }, [load]);
  const emailNow = async (id) => {
    setSending(id);
    try { const { data } = await api.post(`/backups/${id}/email`); toast.success(`Backup emailed to ${data.emailed_to.join(", ")}`); } catch (e) { toast.error(errMsg(e)); } finally { setSending(""); load(); }
  };
  const runNow = async () => {
    setBusy(true);
    try { await api.post("/backups"); toast.success("Backup stored"); load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="md:col-span-2 bg-white rounded-xl border p-5" data-testid="stored-backups">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <div className="font-semibold flex items-center gap-2"><Clock className="h-4 w-4 text-blue-800" /> Stored backups</div>
          <p className="text-sm text-slate-500 mt-1">A full backup is saved automatically every night at 2:00 AM IST (latest 30 kept). Manual and "Before restore" backups are also listed here (latest 20 of each kept).</p>
        </div>
        <Button variant="outline" onClick={runNow} disabled={busy} data-testid="backup-now-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Back up now</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500"><tr><th className="px-3 py-2 text-left">Created</th><th className="px-3 py-2 text-left">Type</th><th className="px-3 py-2 text-right">Employees</th><th className="px-3 py-2 text-right">Transactions</th><th className="px-3 py-2 text-right">Size</th><th className="px-3 py-2 text-left">Email</th><th className="px-3 py-2" /></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="text-center text-slate-500 py-6">No stored backups yet. The first nightly backup runs tonight.</td></tr>}
            {rows.map((b) => (
              <tr key={b.id} className="border-t">
                <td className="px-3 py-2 whitespace-nowrap">{fmtDateTime(b.created_at)}</td>
                <td className="px-3 py-2"><span className={`text-xs rounded-full px-2 py-0.5 whitespace-nowrap ${(KIND[b.kind] || KIND.manual)[1]}`}>{(KIND[b.kind] || KIND.manual)[0]}</span></td>
                <td className="px-3 py-2 text-right money">{b.employees}</td>
                <td className="px-3 py-2 text-right money">{b.transactions}</td>
                <td className="px-3 py-2 text-right text-xs text-slate-500">{(b.size / 1024).toFixed(1)} KB</td>
                <td className="px-3 py-2 whitespace-nowrap space-x-1"><EmailStatus b={b} /><span title={b.storage_status || "Not yet copied"} data-testid={`backup-cloud-${b.id}`} className={`text-xs rounded-full px-2 py-0.5 ${b.storage_status === "stored" ? "bg-sky-50 text-sky-700" : "bg-amber-50 text-amber-700"}`}>{b.storage_status === "stored" ? "Cloud copy" : "No cloud copy"}</span></td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <Button variant="ghost" size="sm" title="Restore this backup" className="text-red-700 hover:text-red-800" onClick={() => setRestoring(b)} data-testid={`stored-backup-restore-${b.id}`}><RotateCcw className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" title="Email this backup" disabled={!!sending} onClick={() => emailNow(b.id)} data-testid={`stored-backup-email-${b.id}`}>{sending === b.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}</Button>
                  <Button variant="ghost" size="sm" onClick={() => download(`/backups/${b.id}/download`).catch(() => toast.error("Download failed"))} data-testid={`stored-backup-download-${b.id}`}><Download className="h-4 w-4" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <RestoreStoredDialog backup={restoring} onOpenChange={(o) => !o && setRestoring(null)} onDone={load} />
    </div>
  );
}

export default function Settings() {
  const { can } = useAuth();
  const admin = can("admin");
  return (
    <div>
      <PageHeader title="Settings" subtitle={admin ? "Account, users, audit trail and backups" : "Your account"} />
      <Tabs defaultValue="account">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="account" data-testid="settings-tab-account">My account</TabsTrigger>
          {admin && <TabsTrigger value="users" data-testid="settings-tab-users">Users</TabsTrigger>}
          {admin && <TabsTrigger value="audit" data-testid="settings-tab-audit">Audit trail</TabsTrigger>}
          {admin && <TabsTrigger value="backup" data-testid="settings-tab-backup">Backup & restore</TabsTrigger>}
          {admin && <TabsTrigger value="printing" data-testid="settings-tab-printing">Slip printing</TabsTrigger>}
        </TabsList>
        <TabsContent value="account"><MyAccount /></TabsContent>
        {admin && <TabsContent value="users"><UsersTab /></TabsContent>}
        {admin && <TabsContent value="audit"><AuditTab /></TabsContent>}
        {admin && <TabsContent value="backup"><BackupTab /></TabsContent>}
        {admin && <TabsContent value="printing"><PrintingTab /></TabsContent>}
      </Tabs>
    </div>
  );
}
